import {Button, Checkbox, Col, Dropdown, Input, Modal, Pagination, Row, Space, Switch, Typography} from "antd";
import {showInfoModal} from "@lib/showModal";
import {DiamondIcon, FlameIcon, GridIcon, InfoIcon, KeyIcon, ListIcon, ListNoImageIcon} from "@components/icons/Icons";
import {DebounceInput} from "@components/debounce-input";
import {DownOutlined, RightOutlined, SearchOutlined, WarningOutlined} from "@ant-design/icons";
import {formatNumber} from "@lib/format";
import {ClearFiltersBar} from "@components/issues-filter-drawer";
import {SortIcon} from "@components/sort-icon";
import {KeyIssueAccordion} from "@components/key-info-accordion";
import {IssueGrid} from "@components/grid";
import {IssuesGridItem, IssuesListItem} from "@mwPages/issues/index";
import {PAGINATION_NONE, PAGINATION_PAGES} from "@components/grid/Grid";
import {scrollToThen} from "@lib/utils";
import SingleResultIssueVariants from "@mwPages/issues/SingleResultIssueVariants";
import {getWordpressUrl} from "@lib/wordpressVars";
import no_search_results_img from "@images/no-search-results.png";
import {AddEditComicDrawer} from "@components/add-edit-comic-drawer";
import React, {useEffect, useMemo, useState} from "react";
import {useWindowSize} from "@components/window-size-provider";
import {useWishlistAddIssueMutation, useWishlistAddIssuesFromFiltersMutation} from "@mwServices/User";
import {showResponse} from "@services/Api";
import {ListViewColumns} from "@mwPages/issues/IssuesListItem";
import {checkFiltersMatch} from "@components/issues-filter-drawer/ClearFiltersBar";
import usePreferences from "@hooks/usePreferences";
import useSubscriptionType from "@hooks/useSubscriptionType";
import useIsLoggedIn from "@hooks/useIsLoggedIn";
import BarcodeFileInput from "@components/barcode-read-provider/BarcodeFileInput";
import {TopnavSearchDropdown} from "@components/topnav-search-dropdown";

const NO_RESULTS_TEXT = "NO_RESULTS_TEXT";
const NO_RESULTS_BARCODE = "NO_RESULTS_BARCODE";
const NO_RESULTS_FILTERS = "NO_RESULTS_FILTERS";

interface IssuesResultsProps {
    issueQuery: any;
    issuesTitle: any;
    infoModalData: {
        title: string;
        content: any;
    } | null;
    defaultFilters: any;
    issueParams: any;
    setIssueParams: (newValue: any) => void;
    search: string;
    setSearch: (newValue: string) => void;
    query?: string;
    setQuery?: (newValue: string) => void;
    selectedComicIds: number[];
    setSelectedComicIds: (newValue: any) => void;
    filterDrawer?: any;
    isSearchBarcode: boolean;
    isSearchES: boolean;
    useGridView: boolean;
    useListView: boolean;
    useListViewNoImages: boolean;
    showIssueNotes: boolean;
    shopLink?: {
        slug: string;
        link_url: string;
        image: string;
        active: boolean;
        subtitle?: string;
    } | null;
}

const IssuesResults = ({
        issueQuery,
        issuesTitle,
        infoModalData,
        defaultFilters,
        issueParams,
        setIssueParams,
        search,
        setSearch,
        query,
        setQuery,
        selectedComicIds,
        setSelectedComicIds,
        filterDrawer,
        isSearchBarcode,
        isSearchES,
        useGridView,
        useListView,
        useListViewNoImages,
        showIssueNotes,
        shopLink,
    }: IssuesResultsProps) => {

    const windowDimensions = useWindowSize();
    const {
        setShowListView,
        itemCount,
        setItemCount,
        setShowListViewNoImages
    } = usePreferences();

    const preferences = usePreferences();
    const isLoggedIn = useIsLoggedIn();

    const showListView = useMemo(() => useListView && preferences.showListView || !useGridView && !useListViewNoImages, [useListView, useGridView, useListViewNoImages, preferences]);
    const showListViewNoImages = useMemo(() => useListViewNoImages && preferences.showListViewNoImages, [useListViewNoImages, preferences]);

    const showViewButtons = useMemo(() => {
        let count = 0;
        if(useGridView) count++;
        if(useListView) count++;
        if(useListViewNoImages) count++;
        return count > 1;
    }, [useListView, useGridView, useListViewNoImages, preferences]);

    const subscriptionType = useSubscriptionType();
    const [showEditMultiple, setShowEditMultiple] = useState(false);
    const [showSearch, setShowSearch] = useState(false);
    const [addToCollectionIds, setAddToCollectionIds] = useState<number[]>([]);

    const {issueData, pagination}  = useMemo(() => {
        if(issueQuery.data) return {
            issueData: issueQuery.data,
            pagination: issueQuery.data.pagination,
        };
        return { issueData: null, pagination: null };
    }, [issueQuery]);

    const showDesktopView = useMemo<boolean>(() => {
        return windowDimensions.over(799);
    }, [windowDimensions]);

    const showValueRibbon = useMemo(() => (
            subscriptionType.isStandard ||
            subscriptionType.isTrial ||
            subscriptionType.isUnlimited),
        [subscriptionType]
    );

    const smallButtons = useMemo(() => {
        return windowDimensions.under(1200);
    }, [windowDimensions]);

    const toggleSelected = (id: number) => {
        setSelectedComicIds(currentIds => {
            if(currentIds.indexOf(id) === -1) {
                return [...currentIds, id];
            } else {
                return currentIds.filter((item) => item !== id);
            }
        })
    };

    const [selectAllType, setSelectAllType] = useState('page');
    useEffect(() => {
        if(selectAllType === 'all' && issueData){
            setSelectedComicIds(issueData.issues.map(issue => issue.id) as number[]);
        }
    }, [selectAllType, issueData]);

    //If user deselects any of the issues on this page, and the select type is 'all', revert to 'page'
    useEffect(() => {
        if(selectAllType === 'all'){
            if(issueData && selectedComicIds.length !== issueData.issues.length){
                setSelectAllType('page');
            }
        }
    }, [selectedComicIds]);

    const [wishlistIssueAdd, addWishlistResponse] = useWishlistAddIssueMutation();
    useEffect(() => {
        // if (addWishlistResponse.isSuccess) onIssuesUpdateBulk(selectedComicIds, {in_wishlist: true});
        showResponse(addWishlistResponse, 'Issues added to wishlist.', 'Failed to add issues to wishlist.');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addWishlistResponse]);

    const [wishlistIssueAddByFilters, addWishlistByFiltersResponse] = useWishlistAddIssuesFromFiltersMutation();
    useEffect(() => {
        // if (addWishlistResponse.isSuccess) onIssuesUpdateBulk(selectedComicIds, {in_wishlist: true});
        showResponse(addWishlistByFiltersResponse, 'Issues added to wishlist.', 'Failed to add issues to wishlist.');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addWishlistByFiltersResponse]);

    //Try to dynamically adjust the column spans for screen size and current settings.
    const listViewColumns = useMemo<ListViewColumns>(() => {
        const colWidths: any = {};
        colWidths.editMultiple = 1;
        colWidths.bookImage = 2;

        let issueColSpan = 8;
        if(showEditMultiple) issueColSpan -= colWidths.editMultiple;
        if(showListViewNoImages) issueColSpan += colWidths.bookImage;
        if(smallButtons) issueColSpan -= 1;

        colWidths.issueDetails = issueColSpan;
        colWidths.year = smallButtons ? 1 : 2;
        colWidths.grade = 2;
        colWidths.rawFmv = smallButtons ? 3 : 2
        colWidths.slabFmv = smallButtons ? 3 : 2;
        colWidths.ebay = 2;
        colWidths.priceTable = 2;
        colWidths.actions = 1;
        colWidths.issueLink = 1;

        return colWidths;
    }, [smallButtons, showEditMultiple, showListViewNoImages]);

    const listViewColumnsSort = useMemo(() => {
        const colSorts: any = {};

        colSorts.issueDetails = isSearchES ? {asc: 1, desc: 2} : {asc: 0, desc: 10};
        colSorts.year = isSearchES ? {asc: 7, desc: 8} : {asc: 6, desc: 28};

        return colSorts;
    }, [isSearchES]);

    const isMobileSingleResult = useMemo(() => (!showDesktopView && pagination && pagination.total_items === 1), [showDesktopView, pagination]);

    const showNoResults = useMemo(() => {
        if(!(issueQuery.isLoading || issueQuery.isFetching) && !(pagination && pagination.total_items !== 0)){
            if(!checkFiltersMatch(defaultFilters, issueParams.filters)) return NO_RESULTS_FILTERS;
            if(isSearchBarcode) return NO_RESULTS_BARCODE;
            return NO_RESULTS_TEXT;
        }
        return false;
    }, [pagination]);

    const [newSearch, setNewSearch] = useState(''+query);

    return (
        <div className={"issues-page"}>
            <Row justify="space-between">
                <Col span={24}>
                    <div className="mar-5">
                        {isSearchBarcode ? (
                            <>
                                <Row className={"buttonsRow"}>
                                    <Col span={showDesktopView ? 12 : 16}>
                                        <Space>
                                            {showViewButtons && (<>
                                                {useGridView && (
                                                    <Button icon={<GridIcon/>} size="large"
                                                            className={"greyButton" + (showListView ? '' : ' selected')}
                                                            onClick={() => setShowListView(false)}/>
                                                )}
                                                {useListView && (
                                                    <Button icon={<ListIcon/>} size="large"
                                                            className={"greyButton" + ((showListView && !showListViewNoImages) ? ' selected' : '')}
                                                            onClick={() => {
                                                                setShowListView(true);
                                                                setShowListViewNoImages(false);
                                                            }}/>
                                                )}
                                                {useListViewNoImages && (
                                                    <Button icon={<ListNoImageIcon/>} size="large"
                                                            className={"greyButton" + ((showListView && showListViewNoImages) ? ' selected' : '')}
                                                            onClick={() => {
                                                                setShowListView(true);
                                                                setShowListViewNoImages(true);
                                                            }}/>
                                                )}
                                            </>)}

                                            {isLoggedIn && (
                                                <Button
                                                    className={"greyButton " + (showEditMultiple ? ' selectedBlue' : '')}
                                                    size={"large"} onClick={() => setShowEditMultiple(!showEditMultiple)}>
                                                    <span className="filterButtonText">+ Add</span>
                                                </Button>
                                            )}
                                        </Space>
                                    </Col>
                                    <Col span={showDesktopView ? 12 : 8} className="flex-align-end">
                                    </Col>
                                </Row>

                                <hr className={(showDesktopView && !smallButtons) ? '' : 'mar-top-5 mar-bot-5'}/>
                            </>
                            ) : (
                            <>
                                <Row style={{minHeight: showDesktopView ? 75 : 35}}>
                                    <Col span={showDesktopView ? 12 : 24} className={"flex-vertical-center"}>
                                        <span className={'total-issues'}>{issuesTitle}</span>

                                        {infoModalData && (
                                            <Button
                                                onClick={() => showInfoModal(infoModalData)}
                                                type="link"
                                                className={"mar-left-10"}
                                                icon={<InfoIcon className={showDesktopView ? "iconLarge" : ""}/>}
                                            />
                                        )}

                                        <Switch
                                            className="variantsToggle mar-left-20"
                                            checkedChildren="Variants"
                                            unCheckedChildren="Variants"
                                            checked={!!issueParams.filters.showVariants}
                                            onChange={(checked) => setIssueParams({
                                                ...issueParams,
                                                filters: {...issueParams.filters, showVariants: checked}
                                            })}
                                        />
                                    </Col>
                                    {showDesktopView && (
                                        <Col span={12} className="flex-align-end flex-vertical-center">
                                            <DebounceInput
                                                size="large"
                                                placeholder={"Filter results..."}
                                                className="searchInput topSearchInput"
                                                prefix={<SearchOutlined/>}
                                                value={search}
                                                maxLength={100}
                                                debounceTimeout={2000}
                                                onChange={(event) => {
                                                    const {value} = event.target;
                                                    if (value !== search) setSearch(value);
                                                }}
                                            />
                                        </Col>
                                    )}
                                </Row>
                                <Row className={"buttonsRow"}>
                                    <Col span={showDesktopView ? 12 : 16}>
                                        <Space>
                                            {showViewButtons && (<>
                                                {useGridView && (
                                                    <Button icon={<GridIcon/>} size="large"
                                                            className={"greyButton" + (showListView ? '' : ' selected')}
                                                            onClick={() => setShowListView(false)}/>
                                                )}
                                                {useListView && (
                                                    <Button icon={<ListIcon/>} size="large"
                                                            className={"greyButton" + ((showListView && !showListViewNoImages) ? ' selected' : '')}
                                                            onClick={() => {
                                                                setShowListView(true);
                                                                setShowListViewNoImages(false);
                                                            }}/>
                                                )}
                                                {useListViewNoImages && (
                                                    <Button icon={<ListNoImageIcon/>} size="large"
                                                            className={"greyButton" + ((showListView && showListViewNoImages) ? ' selected' : '')}
                                                            onClick={() => {
                                                                setShowListView(true);
                                                                setShowListViewNoImages(true);
                                                            }}/>
                                                )}
                                            </>)}

                                            {isLoggedIn && (
                                                <Button
                                                    className={"greyButton " + (showEditMultiple ? ' selectedBlue' : '')}
                                                    size={"large"} onClick={() => setShowEditMultiple(!showEditMultiple)}>
                                                    <span className="filterButtonText">+ Add</span>
                                                </Button>
                                            )}
                                        </Space>
                                    </Col>
                                    <Col span={showDesktopView ? 12 : 8} className="flex-align-end">
                                        <Space>
                                            {showDesktopView && (
                                                <>
                                                    <Button
                                                        icon={<div className="valueBadge hotIssue isActive mar-right-5">
                                                            <FlameIcon/></div>}
                                                        size="large"
                                                        className={"greyButton " + (issueParams.filters.isHot ? ' selected' : '')}
                                                        onClick={() => setIssueParams({
                                                            ...issueParams,
                                                            page: 1,
                                                            filters: {
                                                                ...issueParams.filters,
                                                                isHot: !issueParams.filters.isHot,
                                                                isKey: false,
                                                                isRare: false
                                                            }
                                                        })}
                                                    >
                                                        {issueData ? formatNumber(issueData.overview.is_hot) : "0"}
                                                    </Button>
                                                    <Button
                                                        icon={<div className="valueBadge keyIssue isActive mar-right-5">
                                                            <KeyIcon/></div>}
                                                        size="large"
                                                        className={"greyButton " + (issueParams.filters.isKey ? ' selected' : '')}
                                                        onClick={() => setIssueParams({
                                                            ...issueParams,
                                                            page: 1,
                                                            filters: {
                                                                ...issueParams.filters,
                                                                isHot: false,
                                                                isKey: !issueParams.filters.isKey,
                                                                isRare: false
                                                            }
                                                        })}
                                                    >
                                                        {issueData ? formatNumber(issueData.overview.is_key) : "0"}
                                                    </Button>
                                                    <Button
                                                        icon={<div className="valueBadge rareIssue isActive mar-right-5">
                                                            <DiamondIcon/></div>}
                                                        size="large"
                                                        className={"greyButton " + (issueParams.filters.isRare ? ' selected' : '')}
                                                        onClick={() => setIssueParams({
                                                            ...issueParams,
                                                            page: 1,
                                                            filters: {
                                                                ...issueParams.filters,
                                                                isHot: false,
                                                                isKey: false,
                                                                isRare: !issueParams.filters.isRare,
                                                            }
                                                        })}
                                                    >
                                                        {issueData ? formatNumber(issueData.overview.is_rare) : "0"}
                                                    </Button>
                                                </>
                                            )}

                                            {!showDesktopView && (
                                                <Button className="greyButton " size="large"
                                                        onClick={() => setShowSearch(!showSearch)}>
                                                    <SearchOutlined/>
                                                </Button>
                                            )}

                                            {filterDrawer}

                                            {showDesktopView && pagination && (
                                                <Pagination
                                                    style={{whiteSpace: "nowrap"}}
                                                    current={pagination.current_page}
                                                    pageSize={pagination.per_page}
                                                    total={pagination.total_items}
                                                    onChange={(page) => {
                                                        if (issueParams && issueParams.page !== page) {
                                                            setIssueParams({...issueParams, page: page});
                                                        }
                                                    }}
                                                    simple
                                                />
                                            )}
                                        </Space>
                                    </Col>
                                </Row>

                                {!showDesktopView && (
                                    <Row className={"top-line mar-top-5"}>
                                        <Col span={8}>
                                            <Space>
                                                <Button
                                                    icon={<div className="valueBadge hotIssue isActive mar-right-5"><FlameIcon/>
                                                    </div>}
                                                    className={"greyButton " + (issueParams.filters.isHot ? ' selected' : '')}
                                                    size="small"
                                                    onClick={() => setIssueParams({
                                                        ...issueParams,
                                                        page: 1,
                                                        filters: {
                                                            ...issueParams.filters,
                                                            isHot: !issueParams.filters.isHot,
                                                            isKey: false,
                                                            isRare: false
                                                        }
                                                    })}
                                                >
                                                    {issueData ? formatNumber(issueData.overview.is_hot) : "0"}
                                                </Button>
                                                <Button
                                                    icon={<div className="valueBadge keyIssue isActive mar-right-5"><KeyIcon/>
                                                    </div>}
                                                    className={"greyButton " + (issueParams.filters.isKey ? ' selected' : '')}
                                                    size="small"
                                                    onClick={() => setIssueParams({
                                                        ...issueParams,
                                                        page: 1,
                                                        filters: {
                                                            ...issueParams.filters,
                                                            isHot: false,
                                                            isKey: !issueParams.filters.isKey,
                                                            isRare: false
                                                        }
                                                    })}
                                                >
                                                    {issueData ? formatNumber(issueData.overview.is_key) : "0"}
                                                </Button>
                                                <Button
                                                    icon={<div className="valueBadge rareIssue isActive mar-right-5">
                                                        <DiamondIcon/></div>}
                                                    className={"greyButton " + (issueParams.filters.isRare ? ' selected' : '')}
                                                    size="small"
                                                    onClick={() => setIssueParams({
                                                        ...issueParams,
                                                        page: 1,
                                                        filters: {
                                                            ...issueParams.filters,
                                                            isHot: false,
                                                            isKey: false,
                                                            isRare: !issueParams.filters.isRare,
                                                        }
                                                    })}
                                                >
                                                    {issueData ? formatNumber(issueData.overview.is_rare) : "0"}
                                                </Button>
                                            </Space>
                                        </Col>

                                        <Col span={16} className="flex-align-end">
                                            <Space>
                                                {pagination && (
                                                    <Pagination
                                                        current={pagination.current_page}
                                                        pageSize={pagination.per_page}
                                                        total={pagination.total_items}
                                                        onChange={(page) => {
                                                            if (issueParams && issueParams.page !== page) {
                                                                setIssueParams({...issueParams, page: page});
                                                            }
                                                        }}
                                                        simple
                                                    />
                                                )}
                                            </Space>
                                        </Col>
                                    </Row>
                                )}

                                {showSearch && (
                                    <Row className={"top-line"}>
                                        <Col span={24}>
                                            <DebounceInput
                                                placeholder="Filter results..."
                                                className="searchInput"
                                                prefix={<SearchOutlined/>}
                                                value={search}
                                                maxLength={100}
                                                debounceTimeout={2000}
                                                onChange={(event) => {
                                                    const {value} = event.target;
                                                    if (setSearch && (value !== search)) setSearch(value);
                                                }}
                                            />
                                        </Col>
                                    </Row>
                                )}

                                <ClearFiltersBar
                                    filters={issueParams.filters}
                                    defaultFilters={defaultFilters}
                                    issueCount={issueData ? issueData.overview.total : 0}
                                    onClear={() => {
                                        setSearch('');
                                        setIssueParams({...issueParams, page: 1, filters: defaultFilters});
                                    }}
                                />

                                <hr className={(showDesktopView && !smallButtons) ? '' : 'mar-top-5 mar-bot-5'}/>
                            </>
                        )}

                        {showEditMultiple && (
                            <Row
                                className={'edit-multiple-row ' + (showDesktopView ? 'large-screen ' : '')}>
                                <Col span={24}
                                     className={"edit-multiple-col " + ((showDesktopView && !smallButtons) ? ' mar-bot-10 ' : ' mar-top-5')}>
                                    <Checkbox
                                        checked={issueData && selectedComicIds.length === issueData.issues.length}
                                        onChange={() => {
                                            if (issueData) {
                                                if (selectedComicIds.length === issueData.issues.length) {
                                                    setSelectedComicIds([]);
                                                } else {
                                                    setSelectedComicIds(issueData.issues.map(issue => issue.id) as number[]);
                                                }
                                            }
                                        }}
                                    />

                                    <Dropdown
                                        className="mar-left-10"
                                        menu={{
                                            items: [
                                                {key: 'page', label: `This Page (${(issueData?.issues.length || 0)})`},
                                                {key: 'all', label: `All Pages (${(issueData?.overview.total || 0)})`}],
                                            selectable: true,
                                            defaultSelectedKeys: ['page'],
                                            selectedKeys: [selectAllType],
                                            onSelect: ({key}) => setSelectAllType(key)
                                        }}
                                    >
                                        <Typography.Link>
                                            <Space>
                                                {selectAllType === 'all' ? 'All (' + (issueData?.overview.total || 0) + ')' : '(' + selectedComicIds.length + ')'}
                                                <DownOutlined/>
                                            </Space>
                                        </Typography.Link>
                                    </Dropdown>

                                    <Button
                                        className="greyButton mar-top-5 edit-multi-item"
                                        size={showDesktopView ? "large" : 'middle'}
                                        disabled={selectedComicIds.length === 0}
                                        onClick={() => setAddToCollectionIds(selectedComicIds)}
                                    >
                                        Add to Collection
                                    </Button>

                                    <Button
                                        className="greyButton mar-top-5 edit-multi-item"
                                        size={showDesktopView ? "large" : 'middle'}
                                        disabled={selectedComicIds.length === 0}
                                        onClick={() => Modal.confirm({
                                            autoFocusButton: null,
                                            icon: <WarningOutlined/>,
                                            content: (<div>
                                                <div><b>Add To Wishlist</b></div>
                                                <div className="pad-top-10">
                                                    Are you sure?<br/>
                                                    Selecting this option will add these issues to your wishlist.
                                                </div>
                                            </div>),
                                            onOk() {
                                                if (selectAllType === 'all') {
                                                    wishlistIssueAddByFilters(issueParams);
                                                } else {
                                                    wishlistIssueAdd(selectedComicIds);
                                                }
                                            },
                                        })}
                                    >
                                        Add To Wishlist
                                    </Button>
                                </Col>
                            </Row>
                        )}

                        {!showNoResults && (
                            <>
                                {showListView ? (
                                    <>
                                        {showDesktopView && (
                                            <>
                                                {showListViewNoImages ? (
                                                    <>
                                                        <Row className="list-view-header">
                                                            {showEditMultiple && (
                                                                <Col span={1}>
                                                                </Col>
                                                            )}
                                                            <Col className="no-wrap" span={2}>
                                                                <div className="flex">
                                                                    Issue #
                                                                    <SortIcon
                                                                        sort={issueParams.filters.sort}
                                                                        sortAsc={26}
                                                                        sortDesc={27}
                                                                        onChange={(sort) => setIssueParams({
                                                                            ...issueParams,
                                                                            page: 1,
                                                                            filters: {...issueParams.filters, sort}
                                                                        })}
                                                                    />
                                                                </div>
                                                            </Col>

                                                            <Col className="no-wrap" span={3 }>
                                                            </Col>

                                                            <Col className="no-wrap" span={2}>
                                                                <div className="flex">
                                                                    Year
                                                                    <SortIcon
                                                                        sort={issueParams.filters.sort}
                                                                        sortAsc={6}
                                                                        sortDesc={28}
                                                                        onChange={(sort) => setIssueParams({ ...issueParams, page: 1, filters: { ...issueParams.filters, sort } })}
                                                                    />
                                                                </div>
                                                            </Col>
                                                            <Col className="no-wrap" span={3}>
                                                                <div className="flex">
                                                                    Description
                                                                </div>
                                                            </Col>
                                                            <Col className="no-wrap flex-align-center" span={3}>
                                                                <div className="flex">
                                                                    Raw FMV
                                                                    <SortIcon
                                                                        sort={issueParams.filters.sort}
                                                                        sortAsc={29}
                                                                        sortDesc={30}
                                                                        onChange={(sort) => setIssueParams({
                                                                            ...issueParams,
                                                                            page: 1,
                                                                            filters: {...issueParams.filters, sort}
                                                                        })}
                                                                    />
                                                                </div>
                                                            </Col>
                                                            <Col className="no-wrap flex-align-center" span={3}>
                                                                <div className="flex">
                                                                    Slab FMV
                                                                    <SortIcon
                                                                        sort={issueParams.filters.sort}
                                                                        sortAsc={31}
                                                                        sortDesc={32}
                                                                        onChange={(sort) => setIssueParams({
                                                                            ...issueParams,
                                                                            page: 1,
                                                                            filters: {...issueParams.filters, sort}
                                                                        })}
                                                                    />
                                                                </div>
                                                            </Col>
                                                            <Col className="no-wrap" span={2}>
                                                                <div className="flex">
                                                                    Shop
                                                                    <Button
                                                                        onClick={() => {
                                                                            showInfoModal({
                                                                                title: <b>Affiliate Disclosure</b>,
                                                                                content: <p>This site contains affiliate links for which we
                                                                                    may be compensated.</p>,
                                                                            });
                                                                        }}
                                                                        className="info-icon"
                                                                        type="link"
                                                                        icon={<InfoIcon className={`gain-loss-icon`} />}
                                                                    />
                                                                </div>
                                                            </Col>
                                                            <Col className="no-wrap flex-align-center" span={showEditMultiple? 2 : 3}>
                                                            </Col>
                                                            <Col className="no-wrap" span={1}>
                                                            </Col>
                                                        </Row>
                                                    </>
                                                ) : (<>
                                                    <Row className="list-view-header">
                                                        {showEditMultiple && (
                                                            <Col className={""} span={listViewColumns.editMultiple}></Col>
                                                        )}

                                                        <Col className={""} span={listViewColumns.bookImage}></Col>

                                                        <Col className="no-wrap pad-left-20 " span={listViewColumns.issueDetails}>
                                                            <div className="flex">
                                                                Issue
                                                                <SortIcon
                                                                    sort={issueParams.filters.sort}
                                                                    sortAsc={listViewColumnsSort.issueDetails.asc}
                                                                    sortDesc={listViewColumnsSort.issueDetails.desc}
                                                                    onChange={(sort) => setIssueParams({
                                                                        ...issueParams,
                                                                        page: 1,
                                                                        filters: {...issueParams.filters, sort}
                                                                    })}
                                                                />
                                                            </div>
                                                        </Col>

                                                        <Col className="no-wrap flex-align-center " span={listViewColumns.year}>
                                                            <div className="flex">
                                                                Year
                                                                <SortIcon
                                                                    sort={issueParams.filters.sort}
                                                                    sortAsc={listViewColumnsSort.year.asc}
                                                                    sortDesc={listViewColumnsSort.year.desc}
                                                                    onChange={(sort) => setIssueParams({
                                                                        ...issueParams,
                                                                        page: 1,
                                                                        filters: {...issueParams.filters, sort}
                                                                    })}
                                                                />
                                                            </div>
                                                        </Col>
                                                        <Col className="no-wrap flex-align-center " span={listViewColumns.grade}>
                                                            <div className="flex">
                                                                Grade
                                                            </div>
                                                        </Col>
                                                        <Col className="no-wrap flex-align-center " span={listViewColumns.rawFmv}>
                                                            <div className="flex">
                                                                Raw FMV
                                                            </div>
                                                        </Col>
                                                        <Col className="no-wrap flex-align-center " span={listViewColumns.slabFmv}>
                                                            <div className="flex">
                                                                Slab FMV
                                                            </div>
                                                        </Col>
                                                        <Col className="no-wrap flex-align-center " span={listViewColumns.ebay}>
                                                            <div className="flex">
                                                                Shop
                                                                <Button
                                                                    onClick={() => {
                                                                        showInfoModal({
                                                                            title: <b>Affiliate Disclosure</b>,
                                                                            content: <p>This site contains affiliate links for which
                                                                                we may be compensated.</p>,
                                                                        });
                                                                    }}
                                                                    className="info-icon"
                                                                    type="link"
                                                                    style={{width: 20}}
                                                                    icon={<InfoIcon className={`gain-loss-icon`}/>}
                                                                />
                                                            </div>
                                                        </Col>
                                                        <Col className="no-wrap flex-align-center "
                                                             span={listViewColumns.priceTable}>
                                                            Price Table
                                                        </Col>
                                                        <Col className="no-wrap " span={listViewColumns.actions}>
                                                        </Col>
                                                        <Col className="no-wrap " span={listViewColumns.issueLink}>
                                                        </Col>
                                                    </Row>
                                                </>)}
                                            </>
                                        )}

                                        {isMobileSingleResult && issueData && (
                                            <div className={"mar-top-10"}>
                                                {issueData.issues[0].key_info && (
                                                    <KeyIssueAccordion
                                                        title="You Found A Key"
                                                        keyInfo={issueData.issues[0].key_info}
                                                        defaultExpanded={false}
                                                    />)}
                                            </div>
                                        )}

                                        <IssueGrid
                                            isListGrid={true}
                                            isLoading={issueQuery.isLoading || issueQuery.isFetching}
                                            data={issueData}
                                            noDataMessage="There are no issues matching this search."
                                            IssueRender={(props) =>
                                                (<>
                                                    <IssuesListItem
                                                        {...props}
                                                        toggleSelected={toggleSelected}
                                                        windowDimensions={windowDimensions}
                                                        setAddIssueId={(issueId) => setAddToCollectionIds(issueId ? [issueId] : [])}
                                                    />
                                                </>)}
                                            getItemProps={(item) => ({
                                                key: item.id,
                                                showValueRibbon,
                                                showDesktopView,
                                                showEditMultiple,
                                                showListViewNoImages,
                                                isSelected: selectedComicIds.indexOf(item.id) !== -1,
                                                listViewColumns,
                                                showIssueNotes,
                                                shopLink: shopLink
                                                    ? {
                                                        ...shopLink,
                                                        link_url: shopLink.link_url.replace(
                                                            "#cp_issue_title#",
                                                            encodeURIComponent(`${item.series_title} ${item.issue_number}`)
                                                        ),
                                                    }
                                                    : undefined,
                                            })}
                                            paginationType={isMobileSingleResult ? PAGINATION_NONE : PAGINATION_PAGES}
                                            onPaginationChange={(page => {
                                                if (issueParams && issueParams.page !== page) {
                                                    scrollToThen({top: 0, behavior: 'smooth'}, () => {
                                                        setIssueParams({...issueParams, page: page});
                                                    });
                                                }
                                            })}
                                            onPaginationSizeChange={(current: number, perPage: number) => {
                                                if (issueParams && issueParams.perPage !== perPage) {
                                                    scrollToThen({top: 0, behavior: 'smooth'}, () => {
                                                        setIssueParams({
                                                            ...issueParams,
                                                            page: 1,
                                                            perPage: perPage,
                                                        });
                                                        setItemCount(perPage);
                                                    });
                                                }
                                            }}
                                        />

                                        {isMobileSingleResult && issueData && (
                                            <SingleResultIssueVariants
                                                issue={issueData.issues[0]}
                                                showValueRibbon={showValueRibbon}
                                                showDesktopView={showDesktopView}
                                                showEditMultiple={showEditMultiple}
                                                toggleSelected={toggleSelected}
                                                windowDimensions={windowDimensions}
                                                setAddToCollectionIds={setAddToCollectionIds}
                                                selectedComicIds={selectedComicIds}
                                                listViewColumns={listViewColumns}
                                                shopLink={shopLink}
                                            />
                                        )}
                                    </>
                                ) : (
                                    <IssueGrid
                                        isLoading={issueQuery.isLoading || issueQuery.isFetching}
                                        data={issueData}
                                        noDataMessage="There are no issues matching this search."
                                        IssueRender={(props) =>
                                            (<>
                                                <IssuesGridItem
                                                    {...props}
                                                    toggleSelected={toggleSelected}
                                                    windowDimensions={windowDimensions}
                                                    setAddIssueId={(issueId) => setAddToCollectionIds(issueId ? [issueId] : [])}
                                                />
                                            </>)}
                                        getItemProps={(item) => ({
                                            key: item.id,
                                            showValueRibbon,
                                            showDesktopView,
                                            showEditMultiple,
                                            showListViewNoImages,
                                            isSelected: selectedComicIds.indexOf(item.id) !== -1,
                                            shopLink: shopLink
                                                    ? {
                                                        ...shopLink,
                                                        link_url: shopLink.link_url.replace(
                                                            "#cp_issue_title#",
                                                            encodeURIComponent(`${item.series_title} ${item.issue_number}`)
                                                        ),
                                                    }
                                                    : undefined,
                                        })}
                                        paginationType={PAGINATION_PAGES}
                                        onPaginationChange={(page => {
                                            if (issueParams && issueParams.page !== page) {
                                                scrollToThen({top: 0, behavior: 'smooth'}, () => {
                                                    setIssueParams({...issueParams, page: page});
                                                });
                                            }
                                        })}
                                        onPaginationSizeChange={(current: number, perPage: number) => {
                                            if (issueParams && issueParams.perPage !== perPage) {
                                                scrollToThen({top: 0, behavior: 'smooth'}, () => {
                                                    setIssueParams({...issueParams, page: 1, perPage: perPage});
                                                    setItemCount(perPage);
                                                });
                                            }
                                        }}
                                    />
                                )}
                            </>
                        )}

                        {showNoResults && (
                            <>
                                <div
                                    className="top-box flex-align-center flex-vertical-center no-results"
                                    style={{
                                        background: `url(${getWordpressUrl(no_search_results_img)})`,
                                        backgroundPosition: showDesktopView ? "50% calc(50% + 50px)" : "50% 50%",
                                        backgroundRepeat: "no-repeat",
                                        backgroundSize: "cover",
                                    }}
                                />
                                <div className={"flex-align-center"}>
                                    {showNoResults === NO_RESULTS_TEXT && (
                                        <div
                                            className={"flex-align-center flex-vertical-center flex-col pad-top-50 pad-bot-50 no-results-text"}>
                                            <div className={"no-results-title"}>Sorry, No Results.</div>
                                            <div className={"no-results-subtitle pad-bot-20"}>Try rephrasing your search below.</div>
                                            <TopnavSearchDropdown query={query} background="none" isOnPage={true} />
                                        </div>
                                    )}
                                    {showNoResults === NO_RESULTS_BARCODE && (
                                        <div
                                            className={"flex-align-center flex-vertical-center flex-col pad-top-50 pad-bot-50 no-results-text"}>
                                            <div className={"no-results-title"}>Sorry, No Barcode Results.</div>
                                            <div className={"no-results-subtitle pad-bot-10"}>Try a text search with the
                                                text box
                                                below.
                                            </div>
                                            <TopnavSearchDropdown query={query} background="none" isOnPage={true} />
                                        </div>
                                    )}
                                    {showNoResults === NO_RESULTS_FILTERS && (
                                        <div
                                            className={"flex-align-center flex-vertical-center flex-col pad-top-50 pad-bot-50 no-results-text"}>
                                            <div className={"no-results-title"}>Sorry, No Results.</div>
                                            <div className={"no-results-subtitle"}>Try applying fewer filters to your
                                                search.
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                        <AddEditComicDrawer
                            addIssueIds={addToCollectionIds}
                            addIssuesByFilters={(selectAllType === 'all') ? issueParams.filters : undefined}
                            addIssuesByFiltersCount={(selectAllType === 'all') ? issueQuery?.data?.overview.total || 0 : undefined}
                            visible={addToCollectionIds.length > 0}
                            onClose={() => {
                                setAddToCollectionIds([]);
                                setSelectedComicIds([]);
                            }}
                        />
                    </div>
                </Col>
            </Row>
        </div>
    );
}

export default IssuesResults;